import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { styled, css, useTheme } from 'styled-components';
import { Media } from '../../theme/Media';
import { Tooltip } from '../../V2/Feedback/Tooltip';
import { useTableContext } from './context';
import MobileLabel from './MobileLabel';
export const TooltipWrapper = styled.span `
  font-size: 13px;
`;
const StatusIndicator = styled.span `
  position: absolute;
  top: 0;
  left: 0;
  @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.lgMin : Media.xlgMin)} {
    width: 4px;
    bottom: 0;
  }
  @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.mdMax : Media.lgMax)} {
    right: 0;
    height: 4px;
  }
  background-color: ${({ $statusColor }) => $statusColor};
`;
const StatusIndicatorCover = styled(StatusIndicator) `
  z-index: 1;
  @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.lgMin : Media.xlgMin)} {
    width: 10px;
  }
`;
export const Wrapper = styled.td `
  position: relative;
  background: ${({ $bgColor }) => $bgColor};
  transition: background 0.3s;
  border-bottom: ${({ $subCell, theme }) => !$subCell && `1px solid ${theme.colors.grey10_1}`};
  white-space: ${({ $nowrap }) => $nowrap && 'nowrap'};
  ${({ $wordBreak }) => $wordBreak === 'all' &&
    css `
      word-break: break-all;
    `}
  ${({ $wordBreak }) => $wordBreak === 'phone' &&
    css `
      @media ${Media.xsMax} {
        word-break: break-all;
      }
    `}
  ${({ $actionCell, $isCollapsedNav }) => $actionCell &&
    css `
      @media ${$isCollapsedNav ? Media.lgMin : Media.xlgMin} {
        width: 1px;
      }
    `}
  ${({ $actionCell, $isCollapsedNav, $mainCell }) => !$actionCell &&
    $mainCell &&
    css `
      @media ${$isCollapsedNav ? Media.lgMin : Media.xlgMin} {
        width: 100%;
      }
    `}
  ${({ $actionCell, $isCollapsedNav, $mainCell, $width }) => !$actionCell &&
    !$mainCell &&
    $width &&
    css `
      @media ${$isCollapsedNav ? Media.lgMin : Media.xlgMin} {
        width: ${`${$width}px`};
      }
    `}
  @media ${Media.smMax} {
    ${({ $sm }) => $sm &&
    css `
        width: ${100 / $sm}%!important;
      `};
  }
  @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.mdMax : Media.lgMax)} {
    display: flex;
    width: ${({ $md }) => ($md ? `${100 / $md}%!important` : '100%!important')};
    ${({ $actionCell }) => $actionCell &&
    css `
        position: absolute;
        background: transparent;
        top: 0;
        right: 0;
        border: none;
        width: 36px !important;
        height: 36px;
        z-index: 1;
      `};
  }
`;
export const Container = styled.div `
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${({ $padding, $actionCell, $subCell, $initialCell }) => $padding
    ? css `
          padding: ${$padding};
        `
    : css `
          padding: ${!$actionCell && !$subCell && !$initialCell && '14px'};
        `}
  position: relative;
  cursor: ${({ onClick }) => onClick && 'pointer'};
  @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.lgMin : Media.xlgMin)} {
    min-height: ${({ $subCell, $initialCell }) => !$subCell && !$initialCell && '60px'};
    align-items: ${({ $actionCell }) => $actionCell && 'flex-end'};
    ${({ $align }) => $align &&
    css `
        align-items: ${$align === 'left' && 'flex-start'};
        align-items: ${$align === 'center' && 'center'};
        align-items: ${$align === 'right' && 'flex-end'};
      `}
    min-width: ${({ $actionCell }) => $actionCell && '46px'};
    min-width: ${({ $minWidth }) => $minWidth && `${$minWidth}px`};
  }
  @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.mdMax : Media.lgMax)} {
    justify-content: flex-start;
    width: 100%;
    min-height: 36px;
    ${({ $inline }) => $inline &&
    css `
        flex-direction: row;
        align-items: center;
      `}
  }
  @media ${Media.smMax} {
    min-height: ${({ $actionCell }) => $actionCell && 'unset'};
  }
`;
const Td = React.forwardRef(function Td({ title, children, className, colSpan, bgColor, inline, type, onClick, minWidth, statusColor, statusTooltip, dataTestId, md, sm, padding, align, mainCell, $nowrap, $width, wordBreak, }, ref) {
    const theme = useTheme();
    const { isCollapsedNav } = useTableContext();
    return (_jsxs(Wrapper, { className: className, colSpan: type === 'sub' ? 1000 : colSpan, "$bgColor": bgColor ?? theme.colors.grey00_1, "$subCell": type === 'sub', "$actionCell": type === 'action', "data-testid": dataTestId, "$md": md, "$sm": sm, "$isCollapsedNav": isCollapsedNav, "$mainCell": mainCell, "$nowrap": $nowrap, "$width": $width, "$wordBreak": wordBreak, ref: ref, children: [statusColor && statusTooltip && (_jsx(Tooltip, { tooltip: _jsx(TooltipWrapper, { children: statusTooltip }), ContainerComponent: StatusIndicatorCover, placement: 'right', containerComponentProps: {
                    $isCollapsedNav: isCollapsedNav,
                }, children: _jsx(StatusIndicator, { "$statusColor": statusColor, "$isCollapsedNav": isCollapsedNav }) })), statusColor && !statusTooltip && _jsx(StatusIndicator, { "$statusColor": statusColor, "$isCollapsedNav": isCollapsedNav }), _jsxs(Container, { onClick: onClick, "$inline": inline, "$actionCell": type === 'action', "$minWidth": minWidth, "$subCell": type === 'sub', "$padding": padding, "$initialCell": type === 'initial', "$align": align, "$isCollapsedNav": isCollapsedNav, children: [title && _jsx(MobileLabel, { "$inline": inline, children: title }), children] })] }));
});
export default React.memo(Td);
